import { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { transformResponse, v3Api } from 'requests/axios'
import { PropValue, Props } from 'types/core/props'

import { addAlert } from 'store/alerts/alerts.actions'
import { AlertVariant } from 'types/alert'

import { MonitoringTask } from '../monitoring-task'
import { selectToken } from '../../../../store/users/users.selectors'

const initialFormData: Props = {
  id: '',
  inn: '',
}

export const useMonitoringTasks = () => {
  const dispatch = useDispatch()
  const token = useSelector(selectToken)
  const [tasks, setTasks] = useState<MonitoringTask[]>([])
  const [formData, setFormData] = useState<Props>({ ...initialFormData })
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const fetchTasks = useCallback(async () => {
    setLoading(true)
    try {
      const response = await v3Api.get('/scheduled-task', {
        headers: { Authorization: `Bearer ${token}` },
      })
      setTasks(response.data.tasks)
    } catch (error) {
      console.error('Ошибка при загрузке задач:', error)
    } finally {
      setLoading(false)
    }
  }, [token])

  const createTask = useCallback(async () => {
    const payload = {
      schedule: '0 0 * * 1',
      action: 'create-order',
      active: true,
      data: {
        type: 'counterparty',
        'counterparty.inn': formData.inn,
        'settings.factorCompanyManagementHasChanged': true,
        'settings.factorCompanyBankAccountBlocked': true,
        'settings.factorCompanyLegalEntityAddressHasChanged': true,
        'settings.factorCompanyArbitrationClaims': true,
        'settings.factorCompanyATIClaims': true,
      },
    }
    setLoading(true)
    try {
      const response = await transformResponse(
        v3Api.post<MonitoringTask>('/scheduled-task', payload, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }),
      )

      setTasks((prev) => (Array.isArray(prev) ? [...prev, response] : [response]))
      setFormData({ ...initialFormData })
      setIsOpen(false)

      dispatch(
        addAlert({
          text: `Задача успешно создана.`,
          lifetime: 3000,
          variant: AlertVariant.SUCCESS,
          header: 'Создание задачи',
        }),
      )
    } catch (error) {
      console.error('Ошибка при создании задачи:', error)
      dispatch(
        addAlert({
          text: `Не удалось создать задачу.`,
          lifetime: 3000,
          variant: AlertVariant.DANGER,
          header: 'Создание задачи',
        }),
      )
    } finally {
      setLoading(false)
    }
  }, [formData.inn, token, dispatch])

  const deleteTaskById = useCallback(
    async (id: string) => {
      setLoading(true)
      try {
        await v3Api.delete('/scheduled-task', {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          data: {
            id,
          },
        })

        await fetchTasks()

        dispatch(
          addAlert({
            text: `Задача успешно удалена.`,
            lifetime: 3000,
            variant: AlertVariant.INFO,
            header: 'Удаление задачи',
          }),
        )
      } catch (error) {
        console.error(`Ошибка при удалении задачи с ID ${id}:`, error)
        dispatch(
          addAlert({
            text: `Не удалось удалить задачу.`,
            lifetime: 3000,
            variant: AlertVariant.DANGER,
            header: 'Удаление задачи',
          }),
        )
      } finally {
        setLoading(false)
      }
    },
    [token, fetchTasks, dispatch],
  )

  useEffect(() => {
    fetchTasks()
  }, [fetchTasks])

  const handleChange = useCallback((value: PropValue, field: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  return {
    loading,
    tasks,
    formData,
    isOpen,
    setIsOpen,
    fetchTasks,
    createTask,
    handleChange,
    deleteTaskById,
  }
}
